import React, { useState, useEffect, useContext } from 'react';
import { FiSearch, FiDownload, FiRefreshCw, FiGrid, FiUsers, FiUser, FiDollarSign, FiZap, FiPlus, FiBell, FiRepeat, FiTrendingUp, FiEdit, FiTrash2 } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { extensionsAPI, statisticsAPI, partnersAPI, usersAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import StripeDetails from '../components/StripeDetails';
import StripeAutomation from '../components/StripeAutomation';
import './Dashboard.css';

// Utility function to display balance with infinity symbol for unlimited values
const displayBalance = (value, isUnlimited = false) => {
  if (isUnlimited || value === -1 || value === null || value === undefined) {
    return '∞';
  }
  return value.toString();
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { partner } = useContext(AuthContext);
  const [extensions, setExtensions] = useState([]);
  const [partners, setPartners] = useState([]);
  const [endUsers, setEndUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all extensions');
  const [activeTab, setActiveTab] = useState('extensions');
  const [downloadingId, setDownloadingId] = useState(null);
  const [stripeModalOpen, setStripeModalOpen] = useState(false);
  const [selectedStripeAccount, setSelectedStripeAccount] = useState(null);
  const [stripeAccounts, setStripeAccounts] = useState([]);
  
  // Pagination state for Extension End Users tab
  const [usersCurrentPage, setUsersCurrentPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  
  // Load Stripe accounts from API on mount
  useEffect(() => {
    const fetchStripeAccounts = async () => {
      try {
        const response = await fetch('/api/stripe/accounts', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setStripeAccounts(data.data || []);
        }
      } catch (error) {
        console.error('Error fetching Stripe accounts:', error);
      }
    };

    fetchStripeAccounts();
  }, []);

  // Load Stripe automations from API on mount
  useEffect(() => {
    const fetchStripeAutomations = async () => {
      try {
        const response = await fetch('/api/stripe/automations', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setStripeAutomations(data.data || []);
        }
      } catch (error) {
        console.error('Error fetching Stripe automations:', error);
      }
    };

    fetchStripeAutomations();
  }, []);
  const [automationModalOpen, setAutomationModalOpen] = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [stripeAutomations, setStripeAutomations] = useState([]);

  // Real statistics data
  const [stats, setStats] = useState({
    extensions: { total: 0, active: 0, whitelabeled: 0, remaining: 0 },
    partners: { total: 0, active: 0, superadmin: 0, admin: 0, whitelabeler: 0, reseller: 0, remaining: 0 },
    users: { total: 0, active: 0, suspended: 0, remaining: 0 }
  });

  // Fetch real statistics from database
  const fetchStatistics = async () => {
    try {
      const response = await statisticsAPI.getDashboardStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      // Keep default values if API fails
    }
  };

  // Fetch extensions from database
  const fetchExtensions = async () => {
    try {
      setLoading(true);
      const data = await extensionsAPI.getAll();
      
      console.log('Extensions API response:', data);
      console.log('Current partner:', partner);
      
      if (data.success && data.extensions) {
        let extensionsList = data.extensions;
        
        // Process extensions to add notification indicators
        extensionsList = extensionsList.map(ext => {
          const extension = { ...ext };
          
          // Check if extension has been updated recently (within last 7 days)
          const updatedAt = new Date(extension.updatedAt || extension.createdAt);
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          
          extension.hasNewVersion = updatedAt > sevenDaysAgo;
          extension.isUpgradeable = extension.isUpgradeable || false;
          
          return extension;
        });
        
        setExtensions(extensionsList);
      } else {
        console.error('Failed to fetch extensions:', data);
        setExtensions([]);
      }
    } catch (error) {
      console.error('Error fetching extensions:', error);
      setExtensions([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch partners from database
  const fetchPartners = async () => {
    try {
      const data = await partnersAPI.getAll();
      
      if (data.success && data.partners) {
        setPartners(data.partners);
      } else {
        console.error('Failed to fetch partners:', data);
        setPartners([]);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      setPartners([]);
    }
  };

  // Fetch end users from database with pagination
  const fetchEndUsers = async (page = 1, search = '') => {
    try {
      setUsersLoading(true);
      const params = {
        page: page,
        limit: 10,
        ...(search && { search: search })
      };
      
      const data = await usersAPI.getAll(params);
      
      if (data.success && data.users) {
        setEndUsers(data.users);
        setUsersCurrentPage(data.currentPage || page);
        setUsersTotalPages(data.totalPages || 1);
        setUsersTotal(data.total || 0);
      } else {
        console.error('Failed to fetch end users:', data);
        setEndUsers([]);
        setUsersCurrentPage(1);
        setUsersTotalPages(1);
        setUsersTotal(0);
      }
    } catch (error) {
      console.error('Error fetching end users:', error);
      setEndUsers([]);
      setUsersCurrentPage(1);
      setUsersTotalPages(1);
      setUsersTotal(0);
    } finally {
      setUsersLoading(false);
    }
  };

  // Initialize data
  useEffect(() => {
    fetchStatistics();
    fetchExtensions();
    fetchPartners();
    fetchEndUsers();
  }, []);

  // Refresh data when returning from CreateExtension page
  useEffect(() => {
    if (location.pathname === '/dashboard') {
      fetchStatistics();
      fetchExtensions();
      fetchPartners();
      fetchEndUsers();
    }
  }, [location.pathname]);

  const filterOptions = [
    'all extensions',
    'new upgrade',
    'ready to whitelabel',
    'normal extension',
    'all whitelabel'
  ];

  // Function to get available tabs based on partner permissions
  const getAvailableTabs = () => {
    const allTabs = [
      { id: 'extensions', label: 'Extensions', icon: FiGrid },
      { id: 'partners', label: 'Partners', icon: FiUsers },
      { id: 'users', label: 'Extension End Users', icon: FiUser },
      { id: 'stripe', label: 'Stripe Details', icon: FiDollarSign },
      { id: 'automation', label: 'Stripe Automation', icon: FiZap }
    ];

    if (!partner) return allTabs;

    // Superadmin and admin can see all tabs
    if (['superadmin', 'admin'].includes(partner.role)) {
      return allTabs;
    }

    // Filter tabs based on partnerType
    switch (partner.partnerType) {
      case 'ceo':
        return allTabs; // CEOs can see all tabs
      case 'whitelabeler':
        return allTabs.filter(tab => tab.id !== 'partners'); // Whitelabelers can't manage partners
      case 'reseller':
        return allTabs.filter(tab => tab.id !== 'partners'); // Resellers can access Extensions, End Users, Stripe Details, and Automation
      default:
        return allTabs.filter(tab => tab.id === 'extensions'); // Default to extensions only
    }
  };

  const tabs = getAvailableTabs();

  const filteredExtensions = extensions.filter(ext => {
    const matchesSearch = ext.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (ext.category && ext.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (ext.description && ext.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterType === 'all extensions' || 
                         (filterType === 'all whitelabel' && ext.isWhitelabeled) ||
                         (filterType === 'new upgrade' && (ext.hasNewVersion || ext.isUpgradeable)) ||
                         (filterType === 'ready to whitelabel' && !ext.isWhitelabeled) ||
                         (filterType === 'normal extension' && !ext.isWhitelabeled && !ext.isUpgradeable);
    return matchesSearch && matchesFilter;
  });

  const handleDownload = async (extension) => {
    try {
      setDownloadingId(extension._id || extension.id);
      
      // Check if extension has a file URL
      if (!extension.fileUrl) {
        toast.error('No file available for download');
        return;
      }

      // If it's a base64 data URL, convert it to a blob and download
      if (extension.fileUrl.startsWith('data:')) {
        // Extract the base64 data from the data URL
        const base64Data = extension.fileUrl.split(',')[1];
        const binaryData = atob(base64Data);
        
        // Convert to Uint8Array
        const bytes = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          bytes[i] = binaryData.charCodeAt(i);
        }
        
        // Create blob and download
        const blob = new Blob([bytes], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = extension.fileName || `${extension.name.replace(/\s+/g, '-').toLowerCase()}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Downloading ${extension.name}`);
      } else {
        // If it's a regular URL, open in new tab
        window.open(extension.fileUrl, '_blank');
        toast.success(`Opening ${extension.name} in new tab`);
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download extension');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleExportCSV = () => {
    // Implement CSV export
    console.log('Exporting CSV');
  };

  const handleRefreshVersions = async () => {
    await fetchExtensions();
    toast.success('Extensions refreshed successfully');
  };

  const handleViewDetails = (extensionId) => {
    window.open(`https://app.chromi.us/details/${extensionId}`, '_blank');
  };

  const handleDeleteExtension = async (extension) => {
    if (window.confirm(`Are you sure you want to delete "${extension.name}"?`)) {
      try {
        // TODO: Implement delete API call
        toast.success(`Extension "${extension.name}" deleted successfully`);
        await fetchExtensions(); // Refresh the list
      } catch (error) {
        console.error('Delete error:', error);
        toast.error('Failed to delete extension');
      }
    }
  };

  const handleDeleteUser = async (user) => {
    if (window.confirm(`Are you sure you want to delete user "${user.name || user.email}"?`)) {
      try {
        const response = await usersAPI.delete(user._id);
        if (response.success) {
          toast.success(`User "${user.name || user.email}" deleted successfully`);
          await fetchEndUsers(usersCurrentPage, searchTerm); // Refresh the list with current page
        } else {
          toast.error(response.message || 'Failed to delete user');
        }
      } catch (error) {
        console.error('Delete user error:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const handleSuspendUser = async (user) => {
    if (window.confirm(`Are you sure you want to suspend user "${user.name || user.email}"? This will prevent them from accessing any extensions.`)) {
      try {
        const response = await usersAPI.suspend(user._id);
        if (response.success) {
          toast.success(`User "${user.name || user.email}" suspended successfully`);
          await fetchEndUsers(usersCurrentPage, searchTerm); // Refresh the list with current page
        } else {
          toast.error(response.message || 'Failed to suspend user');
        }
      } catch (error) {
        console.error('Suspend user error:', error);
        toast.error('Failed to suspend user');
      }
    }
  };

  const handleUnsuspendUser = async (user) => {
    if (window.confirm(`Are you sure you want to unsuspend user "${user.name || user.email}"? This will restore their access to all extensions.`)) {
      try {
        const response = await usersAPI.unsuspend(user._id);
        if (response.success) {
          toast.success(`User "${user.name || user.email}" unsuspended successfully`);
          await fetchEndUsers(usersCurrentPage, searchTerm); // Refresh the list with current page
        } else {
          toast.error(response.message || 'Failed to unsuspend user');
        }
      } catch (error) {
        console.error('Unsuspend user error:', error);
        toast.error('Failed to unsuspend user');
      }
    }
  };

  // Pagination handlers for Extension End Users tab
  const handleUsersPageChange = (page) => {
    setUsersCurrentPage(page);
    fetchEndUsers(page, searchTerm);
  };

  const handleUsersSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setUsersCurrentPage(1);
    fetchEndUsers(1, searchValue);
  };

  const handleDeletePartner = async (partner) => {
    if (window.confirm(`Are you sure you want to delete partner "${partner.firstName} ${partner.lastName}"?`)) {
      try {
        const response = await partnersAPI.delete(partner._id || partner.id);
        if (response.success) {
          toast.success(`Partner "${partner.firstName} ${partner.lastName}" deleted successfully`);
          await fetchPartners(); // Refresh the list
        } else {
          toast.error(response.message || 'Failed to delete partner');
        }
      } catch (error) {
        console.error('Delete partner error:', error);
        toast.error('Failed to delete partner');
      }
    }
  };

  // Helper function to check if partner can perform action
  const canPerformAction = (action) => {
    if (!partner) return false;
    
    switch (action) {
      case 'download':
        // Only allow download for partner roles with ceo or whitelabeler partnerTypes
        return partner.role === 'partner' && ['ceo', 'whitelabeler'].includes(partner.partnerType);
      case 'delete':
        return ['admin', 'superadmin'].includes(partner.role);
      case 'details':
        return true; // Everyone can view details
      default:
        return false;
    }
  };

  const handleCreateExtension = () => {
    navigate('/create-extension');
  };

  // Simple function to handle menu click with boundary detection
  const handleMenuClick = (event) => {
    event.stopPropagation();
    const menuButton = event.currentTarget;
    const menu = menuButton.nextElementSibling;
    
    // Close all other menus first
    document.querySelectorAll('.menu-gen').forEach(m => {
      if (m !== menu) {
        m.style.display = 'none';
      }
    });
    
    // Toggle current menu
    if (menu && menu.classList.contains('menu-gen')) {
      if (menu.style.display === 'block') {
        menu.style.display = 'none';
      } else {
        // Get button position and tab container
        const buttonRect = menuButton.getBoundingClientRect();
        const tabContainer = menuButton.closest('.tab');
        const tabRect = tabContainer.getBoundingClientRect();
        const menuHeight = 200; // Approximate menu height
        
        // Check if there's enough space below the button within the tab container
        if (buttonRect.bottom + menuHeight + 5 < tabRect.bottom) {
          // Show menu below button (default behavior)
          menu.style.top = '100%';
          menu.style.bottom = 'auto';
        } else {
          // Show menu above button when near bottom of tab container
          menu.style.top = 'auto';
          menu.style.bottom = '100%';
        }
        
        menu.style.display = 'block';
      }
    }
  };

  // Function to close all menus when clicking outside
  const handleClickOutside = () => {
    document.querySelectorAll('.menu-gen').forEach(menu => {
      menu.style.display = 'none';
    });
  };

  // Add click outside listener
  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Stripe account management functions
  const handleAddStripeAccount = () => {
    setSelectedStripeAccount(null);
    setStripeModalOpen(true);
  };

  const handleEditStripeAccount = (account) => {
    setSelectedStripeAccount(account);
    setStripeModalOpen(true);
  };

  const handleDeleteStripeAccount = async (account) => {
    if (window.confirm('Are you sure you want to delete this Stripe account?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/stripe/accounts/${account._id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const updatedAccounts = stripeAccounts.filter(acc => acc._id !== account._id);
          setStripeAccounts(updatedAccounts);
          toast.success('Stripe account deleted successfully');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete account');
        }
      } catch (error) {
        console.error('Error deleting Stripe account:', error);
        toast.error(error.message || 'Failed to delete Stripe account');
      }
    }
  };

  const handleSaveStripeAccount = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      
      if (selectedStripeAccount) {
        // Edit existing account
        const response = await fetch(`/api/stripe/accounts/${selectedStripeAccount._id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          const data = await response.json();
          const updatedAccounts = stripeAccounts.map(account =>
            account._id === selectedStripeAccount._id ? data.data : account
          );
          setStripeAccounts(updatedAccounts);
          toast.success('Stripe account updated successfully!');
        } else {
          throw new Error('Failed to update account');
        }
      } else {
        // Add new account
        const response = await fetch('/api/stripe/accounts', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          const data = await response.json();
          setStripeAccounts([...stripeAccounts, data.data]);
          toast.success('Stripe account added successfully!');
        } else {
          throw new Error('Failed to add account');
        }
      }
      
      setStripeModalOpen(false);
      setSelectedStripeAccount(null);
      return Promise.resolve();
    } catch (error) {
      console.error('Error saving Stripe account:', error);
      toast.error('Failed to save Stripe account');
      return Promise.reject(error);
    }
  };

  // Stripe Automation functions
  const handleAddAutomation = () => {
    setSelectedAutomation(null);
    setAutomationModalOpen(true);
  };

  const handleEditAutomation = (automation) => {
    setSelectedAutomation(automation);
    setAutomationModalOpen(true);
  };

  const handleDeleteAutomation = async (automation) => {
    if (window.confirm('Are you sure you want to delete this automation rule?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/stripe/automations/${automation._id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const updatedAutomations = stripeAutomations.filter(auto => auto._id !== automation._id);
          setStripeAutomations(updatedAutomations);
          toast.success('Automation rule deleted successfully');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete automation');
        }
      } catch (error) {
        console.error('Error deleting automation:', error);
        toast.error(error.message || 'Failed to delete automation rule');
      }
    }
  };

  const handleSaveAutomation = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      
      if (selectedAutomation) {
        // Edit existing automation
        const response = await fetch(`/api/stripe/automations/${selectedAutomation._id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          const data = await response.json();
          const updatedAutomations = stripeAutomations.map(automation =>
            automation._id === selectedAutomation._id ? data.data : automation
          );
          setStripeAutomations(updatedAutomations);
          toast.success('Automation updated successfully!');
        } else {
          throw new Error('Failed to update automation');
        }
      } else {
        // Add new automation
        const response = await fetch('/api/stripe/automations', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          const data = await response.json();
          setStripeAutomations([...stripeAutomations, data.data]);
          toast.success('Automation created successfully!');
        } else {
          throw new Error('Failed to create automation');
        }
      }
      
      setAutomationModalOpen(false);
      setSelectedAutomation(null);
      return Promise.resolve();
    } catch (error) {
      console.error('Error saving automation:', error);
      toast.error('Failed to save automation');
      return Promise.reject(error);
    }
  };

  // Count notifications
  const newVersionCount = extensions.filter(ext => ext.hasNewVersion).length;
  const upgradeableCount = extensions.filter(ext => ext.isUpgradeable).length;

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Header Section */}
      <div className="headerSection">
        {/* Statistics */}
        <div className="chromiusStatistics">
          <div className="row">
            <div className="col col-4">
              <div className="dashboardBlocks boxed">
                <h5>Extension Balance</h5>
                <div className="statNumbers">
                  <span>Total : </span>
                  <h4>{displayBalance(stats.extensions.total)}</h4>
                </div>
                <div className="statNumbers">
                  <span>Used : </span>
                  <h4>{displayBalance(stats.extensions.active)}</h4>
                </div>
                <div className="statNumbers">
                  <span style={{ color: '#67D859' }}>Remaining : </span>
                  <h4>{displayBalance(stats.extensions.remaining)}</h4>
                </div>
              </div>
            </div>
            <div className="col col-4">
              <div className="dashboardBlocks boxed">
                <h5>Partner Balance</h5>
                <div className="statNumbers">
                  <span>Total : </span>
                  <h4>{displayBalance(stats.partners.total)}</h4>
                </div>
                <div className="statNumbers">
                  <span>Used : </span>
                  <h4>{displayBalance(stats.partners.active)}</h4>
                </div>
                <div className="statNumbers">
                  <span style={{ color: '#67D859' }}>Remaining : </span>
                  <h4>{displayBalance(stats.partners.remaining, stats.partners.unlimitedBalance > 0)}</h4>
                </div>
              </div>
            </div>
            <div className="col col-4">
              <div className="dashboardBlocks boxed">
                <h5>End User Balance</h5>
                <div className="statNumbers">
                  <span>Total : </span>
                  <h4>{displayBalance(stats.users.total)}</h4>
                </div>
                <div className="statNumbers">
                  <span>Used : </span>
                  <h4>{displayBalance(stats.users.active)}</h4>
                </div>
                <div className="statNumbers">
                  <span style={{ color: '#67D859' }}>Remaining : </span>
                  <h4>{displayBalance(stats.users.remaining, stats.users.unlimitedBalance > 0)}</h4>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h2>
          Extension List
          <span className="inlineFig">
            <svg xmlns="http://www.w3.org/2000/svg" width="13.132" height="5.836" viewBox="0 0 13.132 5.836">
              <path d="M13.132,514.848a.249.249,0,0,1-.076.182l-2.918,2.69a.237.237,0,0,1-.266.046.229.229,0,0,1-.144-.22v-1.7H.243A.234.234,0,0,1,0,515.6v-1.459a.234.234,0,0,1,.243-.243H9.727v-1.7a.22.22,0,0,1,.144-.22.244.244,0,0,1,.266.038l2.918,2.66A.24.24,0,0,1,13.132,514.848Z" transform="translate(0 -511.952)"></path>
            </svg>
          </span>
        </h2>
      </div>

      {/* Data Area */}
      <div className="dataArea-content">
        <div className={`tab tabDashboard ${partner?.partnerType === 'reseller' ? 'reseller-tab' : ''}`}>
          {/* Tab Header */}
          <div className="tabHeader d-flex">
            <div className="searchTab">
              <div className="search-gen">
                <input
                  className="form-control"
                  type="search"
                  placeholder={
                    activeTab === 'users' 
                      ? "Search users..." 
                      : activeTab === 'partners'
                      ? "Search partners..."
                      : "Search extensions..."
                  }
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchTerm(value);
                    
                    // Handle search based on active tab
                    if (activeTab === 'users') {
                      handleUsersSearch(value);
                    } else if (activeTab === 'partners') {
                      // Handle partners search if needed
                      setSearchTerm(value);
                    } else {
                      // Handle extensions search
                      setSearchTerm(value);
                    }
                  }}
                />
                <span className="bgForm">
                  <FiSearch className="search-ico" />
                </span>
              </div>
              <div className="form-control extensionSelect">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  {filterOptions.map(option => (
                    <option key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                      {option === 'new upgrade' && upgradeableCount > 0 && ` (${upgradeableCount})`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="actionTab">
              {partner && (partner.role === 'superadmin' || partner.role === 'admin' || partner.partnerType === 'ceo') && (
                <button className="btn btn-green" onClick={handleCreateExtension}>
                  <FiPlus />
                  Create Extension
                </button>
              )}
              <button className="btn btn-blue" onClick={handleRefreshVersions}>
                <FiRefreshCw />
                Refresh
              </button>
              <button className="btn btn-green" onClick={handleExportCSV}>
                Export CSV
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="selectTab">
            <ul>
              {tabs.map(tab => (
                <li key={tab.id} className={activeTab === tab.id ? 'active' : ''}>
                  <span 
                    className="tabLink"
                    onClick={() => setActiveTab(tab.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    {tab.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Extensions Table */}
          {activeTab === 'extensions' && (
            <div className="table-responsive">
              <table className="table table-gen">
                <thead>
                  <tr>
                    <th scope="col">Extension Name</th>
                    <th scope="col">Category Name</th>
                    <th scope="col">Version</th>
                    <th scope="col">Plans</th>
                    <th scope="col">Partners</th>
                    <th scope="col">End Users</th>
                    <th scope="col">Created At</th>
                    <th scope="col">Updated At</th>
                    <th scope="col">Actions</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExtensions.length > 0 ? (
                    filteredExtensions.map(extension => (
                      <tr key={extension._id || extension.id} className={extension.hasNewVersion ? 'new-version' : ''}>
                        <td className="table-name extensionName">
                          <figure className="table-gen-inLogo">
                            <img 
                              src={extension.icon || 'https://via.placeholder.com/40x40/039BE5/FFFFFF?text=FC'} 
                              alt="figure"
                            />
                          </figure>
                          <div className="nameCellText">
                            <div 
                              className="extension-name-link"
                              onClick={() => navigate(`/extension/${extension._id || extension.id}`)}
                              style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                            >
                              {extension.name}
                            </div>
                            <div className="tagsTd">
                              {extension.isWhitelabeled && (
                                <div className="whiteLabeled">
                                  <svg height="404pt" viewBox="-58 0 404 404.54235" width="404pt" xmlns="http://www.w3.org/2000/svg">
                                    <path d="m277.527344 0h-267.257813c-5.519531 0-10 4.476562-10 10v374.527344c-.007812 7.503906 4.183594 14.378906 10.855469 17.808594 6.675781 3.425781 14.707031 2.828124 20.796875-1.550782l111.976563-80.269531 111.980468 80.265625c6.09375 4.371094 14.117188 4.964844 20.789063 1.539062 6.667969-3.425781 10.863281-10.296874 10.863281-17.792968v-374.527344c0-5.523438-4.480469-10-10.003906-10zm-10 384.523438-117.796875-84.441407c-3.484375-2.496093-8.171875-2.496093-11.652344 0l-117.800781 84.445313v-364.527344h247.25zm0 0"></path>
                                  </svg>
                                  "White-labeled"
                                </div>
                              )}
                              {extension.isUpgradeable && (
                                <div className="upgrade-now" style={{ color: 'rgb(5, 102, 8)', cursor: 'pointer' }}>
                                  <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 512 512">
                                    <path d="M69.816,256H0l93.096,93.096L186.2,256h-69.816c0.224-77.016,62.6-139.4,139.616-139.632 c22.672,0.432,44.952,6,65.16,16.296l34.896-34.896C325.6,80.144,291.176,70.528,256,69.832 C153.296,70.112,70.104,153.296,69.816,256z M395.616,256c-0.224,77.016-62.6,139.4-139.616,139.632 c-22.672-0.432-44.952-6-65.16-16.296l-34.896,34.896c30.456,17.624,64.88,27.24,100.056,27.936 C358.696,441.872,441.88,358.696,442.184,256H512l-93.096-93.096L325.8,256H395.616z"></path>
                                  </svg>
                                  <span>Upgrade Now</span>
                                </div>
                              )}
                            </div>
                            {extension.repoMention && (
                              <div className="repoMention">
                                <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="512" height="512" x="0" y="0" viewBox="0 0 512 512">
                                  <path d="M394.667,128h-64c-5.896,0-10.667,4.52-10.667,10.42v64.042c0,5.9,4.771,10.674,10.667,10.674h10.667v7.849    c0,7.453-3.969,14.468-10.365,18.304l-53.635,32.205V106.399h32c3.875,0,7.448-2.106,9.333-5.504    c1.875-3.388,1.771-7.536-0.292-10.83l-53.333-85.39c-3.896-6.233-14.188-6.233-18.083,0l-53.333,85.39    c-2.063,3.294-2.167,7.442-0.292,10.83c1.885,3.398,5.458,5.504,9.333,5.504h32v230.559L192.302,313.4    c-6.771-3.752-10.969-10.903-10.969-18.648v-22.098c18.802-8.259,32-27.017,32-48.845c0-29.426-23.927-53.143-53.333-53.143    s-53.333,23.717-53.333,53.143c0,21.828,13.198,40.585,32,48.845v22.098c0,23.255,12.615,44.707,32.917,55.974l63.083,35.074    v23.986c-18.802,8.259-32,27.017-32,48.845C202.667,488.057,226.594,512,256,512s53.333-23.943,53.333-53.369    c0-21.828-13.198-40.585-32-48.845v-88.501l75.583-45.389C372.094,264.399,384,243.354,384,220.985v-7.849h10.667    c5.896,0,10.667-4.774,10.667-10.674V138.42C405.333,132.52,400.562,128,394.667,128z" fill="#000000" data-original="#000000"></path>
                                </svg>
                                {extension.repoMention}
                                {extension.version && (
                                  <span title="This is the version assigned by super user." style={{ color: 'black' }}>
                                    (V{extension.version})
                                  </span>
                                )}
                              </div>
                            )}
                            <br />
                          </div>
                        </td>
                        <td className="customPointer href">{extension.category || 'N/A'}</td>
                        <td className="customPointer href">{extension.version || '1.0.0'}</td>
                        <td className="customPointer href">{extension.plans?.length || 0}</td>
                        <td className="customPointer href">{extension.assignedPartners?.length || 0}</td>
                        <td className="customPointer href">{extension.totalUsers || extension.users || 0}</td>
                        <td className="table-date">{extension.createdAt ? new Date(extension.createdAt).toLocaleString() : 'N/A'}</td>
                        <td className="table-date">{extension.updatedAt ? new Date(extension.updatedAt).toLocaleString() : 'N/A'}</td>
                        <td className="text-center">
                          {canPerformAction('download') && (
                            <a 
                              data-title="Download" 
                              target="_blank" 
                              className="btn downloadBtn"
                              onClick={() => handleDownload(extension)}
                              style={{ cursor: 'pointer' }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="22" viewBox="0 0 18 22" className="download">
                                <g transform="translate(-3)">
                                  <path className="a" d="M11.2,16.6a1.041,1.041,0,0,0,1.6,0l6-6.3c.5-.5,0-1.3-.8-1.3H14s.2-4.6,0-7a2.074,2.074,0,0,0-2-2,2.074,2.074,0,0,0-2,2c-.2,2.3,0,7,0,7H6a.874.874,0,0,0-.8,1.4Z"></path>
                                  <path className="a" d="M19,19H5a2.006,2.006,0,0,0-2,2H3a.945.945,0,0,0,1,1H20a.945.945,0,0,0,1-1h0A2.006,2.006,0,0,0,19,19Z"></path>
                                </g>
                              </svg>
                            </a>
                          )}
                        </td>
                        <td>
                          <div className="menu-options">
                            <button className="btnOptions" data-title="Action" onClick={handleMenuClick}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="16" viewBox="0 0 20 16" className="menuTrigger">
                                <g className="a" transform="translate(-2 -4)">
                                  <path d="M20,10H4a2,2,0,0,0,0,4H20a2,2,0,0,0,0-4Z"></path>
                                  <path d="M4,8H16a2,2,0,0,0,0-4H4A2,2,0,0,0,4,8Z"></path>
                                  <path d="M16,16H4a2,2,0,0,0,0,4H16a2,2,0,0,0,0-4Z"></path>
                                </g>
                              </svg>
                            </button>
                            <div className="menu-gen">
                              <button className="btn btn-medium">
                                Plan(s)
                              </button>
                              <button className="btn btn-medium" onClick={() => handleViewDetails(extension._id || extension.id)}>
                                Details
                              </button>
                              <button className="btn btn-medium">
                                Assign and dissolve
                              </button>
                              {extension.isWhitelabeled && (
                                <button className="btn btn-medium">
                                  Update Whitelabel Extension
                                </button>
                              )}
                              {extension.isUpgradeable && (
                                <button className="btn btn-medium">
                                  Upgrade
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center">
                        {searchTerm || filterType !== 'all extensions' 
                          ? 'No extensions match your search criteria' 
                          : 'No extensions found. Click "Create Extension" to add your first extension.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Partners Table */}
          {activeTab === 'partners' && (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Partner</th>
                    <th>Email</th>
                    <th>Company</th>
                    <th>Role</th>
                    <th>Partner Type</th>
                    <th>Balance</th>
                    <th>Extensions</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.length > 0 ? (
                    partners.map(partner => (
                      <tr key={partner._id || partner.id}>
                        <td>
                          <div className="partner-info">
                            <div className="partner-name">
                              {partner.firstName} {partner.lastName}
                            </div>
                            <div className="partner-phone">
                              {partner.phoneNumber || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td>{partner.email}</td>
                        <td>{partner.company || 'N/A'}</td>
                        <td>
                          <span className={`role-badge ${partner.role}`}>
                            {partner.role}
                          </span>
                        </td>
                        <td>
                          <span className={`partner-type-badge ${partner.partnerType}`}>
                            {partner.partnerType}
                          </span>
                        </td>
                        <td>
                          {partner.isBalanceUnlimited ? (
                            <span className="unlimited-badge">∞</span>
                          ) : (
                            <span className="balance-amount">
                              {(() => {
                                // Calculate total balance from all assigned extensions
                                const totalBalance = partner.assignedExtensions?.reduce((total, ext) => {
                                  if (ext.isExtensionBalanceUnlimited) return total;
                                  return total + (ext.extensionBalance || 0);
                                }, 0) || 0;
                                return totalBalance;
                              })()}
                            </span>
                          )}
                        </td>
                        <td>{partner.assignedExtensions?.length || 0}</td>
                        <td>
                          <span className={`status-badge ${partner.status}`}>
                            {partner.status}
                          </span>
                        </td>
                        <td>{partner.date ? new Date(partner.date).toLocaleDateString() : 'N/A'}</td>
                        <td>
                          <div className="menu-options">
                            <button className="btnOptions" data-title="Action" onClick={handleMenuClick}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="16" viewBox="0 0 20 16" className="menuTrigger">
                                <g className="a" transform="translate(-2 -4)">
                                  <path d="M20,10H4a2,2,0,0,0,0,4H20a2,2,0,0,0,0-4Z"></path>
                                  <path d="M4,8H16a2,2,0,0,0,0-4H4A2,2,0,0,0,4,8Z"></path>
                                  <path d="M16,16H4a2,2,0,0,0,0,4H16a2,2,0,0,0,0-4Z"></path>
                                </g>
                              </svg>
                            </button>
                            <div className="menu-gen">
                              <button className="btn btn-medium" onClick={() => navigate(`/partners/${partner._id || partner.id}/edit`)}>
                                Edit Partner
                              </button>
                              <button className="btn btn-medium" onClick={() => handleDeletePartner(partner)}>
                                Delete Partner
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center">
                        No partners found. Click "Add Partner" to create your first partner.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Extension End Users Table */}
          {activeTab === 'users' && (
            <>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Extension</th>
                      <th>Plan</th>
                      <th>Phone #</th>
                      {partner && (partner.role === 'admin' || partner.role === 'superadmin' || partner.partnerType === 'ceo' || partner.partnerType === 'whitelabeler') && (
                      <th>Created By</th>
                    )}
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersLoading ? (
                      <tr>
                        <td colSpan={partner && (partner.role === 'admin' || partner.role === 'superadmin' || partner.partnerType === 'ceo' || partner.partnerType === 'whitelabeler') ? "9" : "8"} className="text-center">
                          <div className="loading-spinner">Loading users...</div>
                        </td>
                      </tr>
                    ) : endUsers.length > 0 ? (
                      endUsers.map(user => (
                        <tr key={user._id}>
                          <td>
                            <div className="user-info">
                              <div className="user-name">
                                {user.name || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td>{user.email}</td>
                          <td>
                            <span className="extension-badge">
                              {user.extensionId?.name || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <span className="plan-badge">
                              {(() => {
                                // Check if the extension has plans
                                const extension = extensions.find(ext => ext._id === user.extensionId?._id);
                                if (!extension || !extension.plans || extension.plans.length === 0) {
                                  return 'N/A';
                                }
                                // If extension has plans, show the user's plan
                                return user.plan !== undefined ? user.plan : 'N/A';
                              })()}
                            </span>
                          </td>
                          <td>{user.phoneNumber || 'N/A'}</td>
                          {partner && (partner.role === 'admin' || partner.role === 'superadmin' || partner.partnerType === 'ceo' || partner.partnerType === 'whitelabeler') && (
                          <td>
                            <span className="created-by-badge">
                              {user.createdBy?.name || user.createdBy?.firstName ? 
                                `${user.createdBy.firstName || ''} ${user.createdBy.lastName || ''}`.trim() || 'N/A' : 
                                'N/A'
                              }
                            </span>
                          </td>
                        )}
                          <td>
                            <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                              {user.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>{user.date ? new Date(user.date).toLocaleDateString() : 'N/A'}</td>
                          <td>
                            <div className="menu-options">
                              <button className="btnOptions" data-title="Action" onClick={handleMenuClick}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="16" viewBox="0 0 20 16" className="menuTrigger">
                                  <g className="a" transform="translate(-2 -4)">
                                    <path d="M20,10H4a2,2,0,0,0,0,4H20a2,2,0,0,0,0-4Z"></path>
                                    <path d="M4,8H16a2,2,0,0,0,0-4H4A2,2,0,0,0,4,8Z"></path>
                                    <path d="M16,16H4a2,2,0,0,0,0,4H16a2,2,0,0,0,0-4Z"></path>
                                  </g>
                                </svg>
                              </button>
                              <div className="menu-gen">
                                <button className="btn btn-medium" onClick={() => navigate(`/users/${user._id}`)}>
                                  View Details
                                </button>
                                <button className="btn btn-medium" onClick={() => navigate(`/users/${user._id}/edit`)}>
                                  Edit User
                                </button>
                                <button className="btn btn-medium" onClick={() => navigate(`/users/${user._id}/plan`)}>
                                  Change Plan
                                </button>
                                <button 
                                  className="btn btn-medium" 
                                  onClick={() => user.isActive ? handleSuspendUser(user) : handleUnsuspendUser(user)}
                                >
                                  {user.isActive ? 'Suspend User' : 'Unsuspend User'}
                                </button>
                                <button className="btn btn-medium" onClick={() => handleDeleteUser(user)}>
                                  Delete User
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={partner && (partner.role === 'admin' || partner.role === 'superadmin' || partner.partnerType === 'ceo' || partner.partnerType === 'whitelabeler') ? "9" : "8"} className="text-center">
                          No end users found. Create end users first to see them here.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination for Extension End Users */}
              {usersTotalPages > 1 && (
                <div className="paginationDiv">
                  <ul className="pagination">
                    <li className={usersCurrentPage === 1 ? 'disabled' : ''}>
                      <a 
                        className="" 
                        href="#" 
                        aria-label="Go to previous page"
                        onClick={(e) => {
                          e.preventDefault();
                          if (usersCurrentPage > 1) {
                            handleUsersPageChange(usersCurrentPage - 1);
                          }
                        }}
                      >
                        <span className="prevs">&lt; Prev</span>
                      </a>
                    </li>
                    
                    {/* Generate page numbers */}
                    {Array.from({ length: usersTotalPages }, (_, i) => i + 1).map(pageNum => (
                      <li key={pageNum} className={pageNum === usersCurrentPage ? 'active' : ''}>
                        <a 
                          className={pageNum === usersCurrentPage ? 'undefined' : ''} 
                          href="#" 
                          aria-label={`Go to page number ${pageNum}`}
                          onClick={(e) => {
                            e.preventDefault();
                            handleUsersPageChange(pageNum);
                          }}
                        >
                          {pageNum}
                        </a>
                      </li>
                    ))}
                    
                    <li className={usersCurrentPage === usersTotalPages ? 'disabled' : ''}>
                      <a 
                        className="" 
                        href="#" 
                        aria-label="Go to next page"
                        onClick={(e) => {
                          e.preventDefault();
                          if (usersCurrentPage < usersTotalPages) {
                            handleUsersPageChange(usersCurrentPage + 1);
                          }
                        }}
                      >
                        <span className="nxts">Next &gt;</span>
                      </a>
                    </li>
                  </ul>
                </div>
              )}
            </>
          )}

          {/* Stripe Details Tab */}
          {activeTab === 'stripe' && (
            <div className="table-responsive">
              <div className="stripe-header">
                <div>
                  <h3>Stripe Accounts</h3>
                  <p>Manage your Stripe accounts for automatic billing integration.</p>
                </div>
                <button className="btn btn-green" onClick={handleAddStripeAccount}>
                  <FiPlus /> Add Stripe Account
                </button>
              </div>
              
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Account Name</th>
                    <th>Public Key</th>
                    <th>Secret Key</th>
                    <th>Status</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stripeAccounts.length > 0 ? (
                    stripeAccounts.map((account) => (
                      <tr key={account.id}>
                        <td>
                          <div className="stripe-account-name">
                            <strong>{account.accountName}</strong>
                          </div>
                        </td>
                        <td>
                          <div className="stripe-account-id">
                            {account.publicKey.substring(0, 20)}...
                          </div>
                        </td>
                        <td>
                          <div className="stripe-account-id">
                            {account.secretKey.substring(0, 20)}...
                          </div>
                        </td>
                        <td>
                          <span className={`status-badge ${account.status}`}>
                            {account.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          {account.createdAt ? new Date(account.createdAt).toLocaleDateString() : 'N/A'}
                        </td>
                        <td>
                          <div className="stripe-actions">
                            <button 
                              className="btn btn-edit" 
                              onClick={() => handleEditStripeAccount(account)}
                              title="Edit Account"
                            >
                              <FiEdit />
                            </button>
                            <button 
                              className="btn btn-delete" 
                              onClick={() => handleDeleteStripeAccount(account)}
                              title="Delete Account"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        <div className="empty-state">
                          <FiDollarSign size={48} />
                          <h4>No Stripe Accounts</h4>
                          <p>Add your first Stripe account to enable automatic billing.</p>
                          <button className="btn btn-green" onClick={handleAddStripeAccount}>
                            <FiPlus /> Add Your First Stripe Account
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Stripe Automation Tab */}
          {activeTab === 'automation' && (
            <div className="table-responsive">
              <div className="automation-header">
                <div>
                  <h3>Stripe Automation</h3>
                  <p>Set up automatic dunning when subscriptions are cancelled in Stripe.</p>
                </div>
                <button className="btn btn-green" onClick={handleAddAutomation}>
                  <FiPlus /> Add
                </button>
              </div>
              
              <table className="table table-striped automation-table">
                <thead>
                  <tr>
                    <th>Automation Name</th>
                    <th>Stripe Account Name</th>
                    <th>Stripe Product IDs</th>
                    <th>Stripe Plan IDs</th>
                    <th>Extensions</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {stripeAutomations.length > 0 ? (
                    stripeAutomations.map((automation) => {
                      const stripeAccount = stripeAccounts.find(acc => acc.id === automation.stripeAccountId);
                      const automationExtensions = extensions.filter(ext => automation.extensionIds.includes(ext._id));
                      
                      return (
                        <tr key={automation.id}>
                          <td>
                            <div className="automation-name">
                              {automation.automationName}
                            </div>
                          </td>
                          <td>
                            <div className="stripe-account-name">
                              {stripeAccount?.accountName || 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div className="product-ids" title={automation.stripeProductIds}>
                              {automation.stripeProductIds.split('\n')[0] || automation.stripeProductIds.split(',')[0] || 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div className="plan-ids" title={automation.stripePlanIds}>
                              {automation.stripePlanIds.split('\n')[0] || automation.stripePlanIds.split(',')[0] || 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div className="extensions-list">
                              {automationExtensions.map(ext => (
                                <span key={ext._id} className="extension-tag">
                                  {ext.name}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td>
                            <div className="automation-actions">
                              <button 
                                className="btn btn-edit" 
                                onClick={() => handleEditAutomation(automation)}
                                title="Edit Automation"
                              >
                                <FiEdit />
                              </button>
                              <button 
                                className="btn btn-delete" 
                                onClick={() => handleDeleteAutomation(automation)}
                                title="Delete Automation"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        <div className="automation-empty-state">
                          <FiZap size={48} />
                          <h4>No Automation Rules</h4>
                          <p>Create automation rules to automatically create accounts when customers subscribe to your Stripe plans.</p>
                          <button className="btn btn-green" onClick={handleAddAutomation}>
                            <FiPlus /> Add Your First Automation Rule
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Other tabs content */}
          {activeTab !== 'extensions' && activeTab !== 'partners' && activeTab !== 'users' && activeTab !== 'stripe' && activeTab !== 'automation' && (
            <div className="tab-content">
              <p>Content for {activeTab} tab will be implemented here.</p>
            </div>
          )}
        </div>
      </div>

      {/* Stripe Details Modal */}
      <StripeDetails
        isOpen={stripeModalOpen}
        onClose={() => setStripeModalOpen(false)}
        onSave={handleSaveStripeAccount}
        stripeAccount={selectedStripeAccount}
      />

      {/* Stripe Automation Modal */}
      <StripeAutomation
        isOpen={automationModalOpen}
        onClose={() => setAutomationModalOpen(false)}
        onSave={handleSaveAutomation}
        automation={selectedAutomation}
        stripeAccounts={stripeAccounts}
        extensions={extensions}
      />
    </div>
  );
};

export default Dashboard; 